<script lang="ts">
  import { _ } from "svelte-i18n";
  import { api } from "$lib/api";

  let email = $state("");
  let loading = $state(false);
  let sent = $state(false);
  let error = $state<string | null>(null);

  async function handleSubmit(e: SubmitEvent) {
    e.preventDefault();
    loading = true;
    error = null;
    try {
      await api.post("/auth/password-reset/request", { email });
      sent = true;
    } catch (err: any) {
      error = err?.message ?? $_("auth.unexpectedError");
    } finally {
      loading = false;
    }
  }
</script>

<div class="auth-page">
  <div class="auth-card">
    <div class="brand">
      <img src="/logo.svg" alt="Order Evolution" class="logo" />
      <h2>{$_("auth.reset.requestTitle")}</h2>
      <p class="subtitle">{$_("auth.reset.requestSubtitle")}</p>
    </div>

    {#if sent}
      <div class="success-message">
        <span class="material-icons">mark_email_read</span>
        <p>{$_("auth.reset.requestSent")}</p>
        <p class="hint"><a href="#/login">{$_("auth.reset.backToLogin")}</a></p>
      </div>
    {:else}
      <form onsubmit={handleSubmit} class="auth-form">
        {#if error}
          <div class="error-alert">{error}</div>
        {/if}

        <div class="field">
          <label for="email">{$_("auth.email")}</label>
          <input
            type="email"
            id="email"
            bind:value={email}
            required
            autocomplete="email"
            placeholder={$_("auth.email.placeholder")}
            disabled={loading}
          />
        </div>

        <button type="submit" class="btn btn--primary btn--lg" disabled={loading || !email}>
          {#if loading}
            <span class="spinner"></span>
            {$_("common.processing")}...
          {:else}
            {$_("auth.reset.sendLink")}
          {/if}
        </button>

        <p class="bottom"><a href="#/login">{$_("auth.reset.backToLogin")}</a></p>
      </form>
    {/if}
  </div>
</div>

<style>
  .auth-page {
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--color-bg);
    padding: 20px;
  }
  .auth-card {
    width: 100%;
    max-width: 440px;
    background: white;
    padding: 40px;
    border-radius: 12px;
    box-shadow: 0 10px 25px rgba(0,0,0,0.05);
  }
  .brand { text-align: center; margin-bottom: 32px; }
  .logo { height: 48px; margin-bottom: 16px; }
  .brand h2 { margin: 0; font-size: 24px; color: var(--color-surface-dark); }
  .subtitle { margin: 8px 0 0; color: var(--color-text-muted); }

  .auth-form { display: flex; flex-direction: column; gap: 20px; }
  .field { display: flex; flex-direction: column; gap: 8px; }
  .field label { font-size: 13px; font-weight: 600; color: var(--color-surface-dark); }
  .field input {
    padding: 12px;
    border: 1px solid var(--color-divider);
    border-radius: 6px;
    font-size: 15px;
    transition: border-color 0.2s;
  }
  .field input:focus { border-color: var(--color-primary); outline: none; }

  .btn--lg { padding: 14px; font-size: 16px; font-weight: 600; border-radius: 6px; cursor: pointer; }
  .btn--primary { background: var(--color-primary); color: white; border: none; }
  .btn--primary:hover { opacity: 0.9; }
  .btn:disabled { opacity: 0.7; cursor: not-allowed; }

  .error-alert {
    padding: 12px;
    background: #fff5f5;
    border: 1px solid #ffc1c1;
    color: #e53935;
    border-radius: 6px;
    font-size: 14px;
    text-align: center;
  }

  .success-message { text-align: center; padding: 20px 0; }
  .success-message .material-icons { font-size: 64px; color: #4caf50; margin-bottom: 16px; }
  .success-message p { margin: 8px 0; font-size: 16px; }
  .success-message .hint { font-size: 14px; color: var(--color-text-muted); }

  .bottom { text-align: center; margin: 0; font-size: 13px; }

  .spinner {
    display: inline-block;
    width: 16px;
    height: 16px;
    border: 2px solid rgba(255,255,255,0.3);
    border-top-color: white;
    border-radius: 50%;
    animation: spin 0.6s linear infinite;
    margin-right: 8px;
    vertical-align: middle;
  }
  @keyframes spin { to { transform: rotate(360deg); } }
</style>
